import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Grid,
} from "@mui/material";
import Swal from "sweetalert2";
import AccountTransferForm from "./form";
import { Refresh, Close } from "@mui/icons-material"; // Import Refresh and Close icons
import { AccountsandFinanceurl, companyManagementUrl, customerservice, setupManagementUrl } from "../../../../config";
import { fetchJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = customerservice.uri;

const CustomerBalanceTransferPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false); // New state for balance loading
  const [customerBalance, setCustomerBalance] = useState("N/A"); // New state for balance
  const [branchData, setBranchData] = useState([]);



  const resetProcess = () => {
    setSearchTerm("");
    setFilteredCustomer(null);
    setShowForm(false);
    setLoadingSearch(false);
    setLoadingBalance(false);
    setCustomerBalance("N/A");


  };
  const fetchCustomerBalance = async (customerId) => {
    try {
      setLoadingBalance(true);
  
      const query = `
        query {
          customersByColumnValues(customerBalanceView: { id: ${customerId} }) {
            error
            results
          }
        }
      `;
  
      const token = localStorage.getItem("token");
      const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;
  
      const response = await axios.post(
        base_url,
        { query },
        {
          headers: {
            Authorization: formattedToken,
            "Content-Type": "application/json",
          },
        }
      );
  
      const data = response.data?.data?.customerBalanceViewByColumnValues;
  
      // Check if results exist and parse it
      if (data?.results) {
        const balanceResults = JSON.parse(data.results); // Parse the JSON array from the string
        if (balanceResults.length) {
          return balanceResults[0].Balance; // Use "Balance" field from the parsed object
        }
      }
  
      // If an error exists, show it
      if (data?.error) {
        Swal.fire("Error", data.error || "Unexpected error occurred.", "error");
      }
    } catch (error) {
      // Handle unexpected errors
      Swal.fire("Error", "An error occurred while fetching the balance.", "error");
      console.error("Error fetching customer balance:", error);
    } finally {
      // Ensure loading state is reset
      setLoadingBalance(false);
    }
  
    return "N/A"; // Default fallback if balance fetch fails
  };
  

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchTerm.length >= 10) {
        const variations = [
          searchTerm,
          searchTerm.startsWith("0") ? searchTerm.replace(/0/, "254") : null,
          searchTerm.startsWith("254") ? searchTerm.replace(/254/, "0") : null,
          searchTerm.startsWith("01") ? searchTerm.replace(/01/, "254") : null, // Handle numbers starting with 01
        ].filter(Boolean);

        setLoadingSearch(true);

        try {
          let foundCustomer = null;

          for (const number of variations) {
            const query = `
              query {
                customersByColumnValues(customer: {mobileNumber: "${number}"}) {
                  error
                  results
                }
              }
            `;

            const token = localStorage.getItem("token");
            const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;

            const response = await axios.post(
              base_url,
              { query },
              {
                headers: {
                  Authorization: formattedToken,
                  "Content-Type": "application/json",
                },
              }
            );

            const data = response.data?.data?.customersByColumnValues;
        
            if (data?.results) {
              const parsedResults = JSON.parse(data?.results);
              if (parsedResults.length) {
                foundCustomer = parsedResults[0];
                break; // Exit the loop if customer is found
              }
            } else if (data?.error) {
              Swal.fire("Error", data.error || "Unexpected error", "error");
              break; // Exit the loop on server-side error
            }
          }

          
          if (foundCustomer) {
            setFilteredCustomer(foundCustomer); // Add balance and collateral to customer object
          } else {
            Swal.fire("Customer Not Found", "Customer does not exist with any variations.", "warning");
          }
          
          if (!foundCustomer) {
            Swal.fire("Customer Not Found", "Customer does not exist with any variations.", "warning");
          }
        } catch (error) {
          Swal.fire("Error", "An error occurred while searching. Please try again.", "error");
          console.error("Error fetching customer by mobile number:", error);
        } finally {
          setLoadingSearch(false);
        }
      } else {
        setFilteredCustomer(null);
        setLoadingSearch(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const branchDataResponse = await fetchJsonDynamic("branch", null, null, companyManagementUrl.uri);
        if (branchDataResponse?.results) {
          const results = JSON.parse(branchDataResponse.results);
          setBranchData(results || []);
        }
      } catch (error) {
        console.error("Error fetching branch data:", error);
        Swal.fire("Error", "Failed to fetch branch data.", "error");
      }
    };
  
    fetchBranches();
  }, []);
  const getBranchName = (branchId) => {
    const branch = branchData.find((branch) => branch.Id === branchId);
    return branch ? branch.Name : "Unknown Branch"; // Fallback to "Unknown Branch" if not found
  };
  
  
  
  return (
    <Box m="20px">
      <Typography variant="h4" gutterBottom>
        Inter Account Transfer
      </Typography>

      <Box mb={3} display="flex" gap={2} alignItems="center">
        <TextField
          label="Enter Customer Mobile Number"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: "300px", // Custom width
            height: "40px", // Custom height
          }}
        />
        <Button variant="contained" disabled={loadingSearch}>
          {loadingSearch ? <CircularProgress size={24} /> : "Search"}
        </Button>
      </Box>

      {filteredCustomer && (
        <Box display="flex" gap={3}>
          {/* Customer Details Card */}
          <Card variant="outlined" sx={{ flex: 1, p: 2, bgcolor: "#f9f9f9" }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    color: "#1976d2", // Header color
                    borderBottom: "2px solid #1976d2", // Underline for header
                    pb: 1,
                    mb: 2,
                  }}
                >
                  Customer Details
                </Typography>
                <IconButton onClick={resetProcess} color="primary">
                  <Refresh />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                {/* Customer Detail Rows */}
                {[
                  { label: "Name", value: `${filteredCustomer.FirstName} ${filteredCustomer.OtherNames}` },
                  { label: "ID/PassportNo", value: filteredCustomer.IdNoOrPassport|| "N/A" },
                  { label: "Mobile", value: filteredCustomer.MobileNumber },
                  { label: "Branch", value: getBranchName(filteredCustomer.BranchId) || "N/A" },
                  { label: "Collateral Balance", value: filteredCustomer.CollateralBalance || 0 },
                  { label: "OLB Balance", value: filteredCustomer.LoanBalance || 0 },
                  {
                    label: "Current Balance",
                    value: loadingBalance ? <CircularProgress size={16} /> : filteredCustomer.CustomerBalance || 0,
                  },                ].map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: "#e0e0e0", // Light grey background for rectangle
                        borderRadius: "8px", // Rounded corners
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)", // Subtle shadow for depth
                        minHeight: "80px", // Ensure rectangles have a consistent height
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "#424242",
                          fontWeight: "bold",
                          wordBreak: "break-word", // Ensure labels wrap properly
                        }}
                      >
                        {item.label}:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          wordBreak: "break-word", // Ensure values wrap properly
                          overflowWrap: "break-word",
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Button
                variant="contained"
                color="primary"
                sx={{
                  mt: 3,
                  bgcolor: "#1976d2",
                  ":hover": { bgcolor: "#1565c0" },
                }}
                onClick={() => setShowForm(true)}
              >
                Transfer Funds
              </Button>
            </CardContent>
          </Card>



          {/* Transfer Funds Form */}
          {showForm && (
            <Box
              flex="1"
              display="flex"
              flexDirection="column"
              border="1px solid #ccc"
              borderRadius="8px"
              padding="16px"
              minWidth="300px"
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" gutterBottom>
                  Transfer Funds
                </Typography>
                <IconButton onClick={() => setShowForm(false)} color="secondary">
                  <Close />
                </IconButton>
              </Box>
              <AccountTransferForm
                data={filteredCustomer}
                onClose={() => setShowForm(false)}
                onAction={() => {
                  setFilteredCustomer(null);
                  setShowForm(false);
                  setSearchTerm("");
                  // fetchCustomerBalance(filteredCustomer.Id);
                }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CustomerBalanceTransferPage;
